import React, {
  useEffect, useRef, useState,
} from "react";

import { CloseOutlined } from "@ant-design/icons";
import { Button, Input, Modal } from "antd";
import { toast } from "react-toastify";

import TestDirectoryRequestSuccessModal from "./TestDirectoryRequestSuccessModal";

import { createTestRequests } from "../../../actions/testDirectory";

import "./TestDirectoryModals.css";

const INITIAL_FORM = {
  test_name: "",
  test_frequency: "",
  test_spec: "",
  test_unit: "",
  test_method: "",
  comment: "",
};

const INPUT_ALLOW_CLEAR = {
  clearIcon: <CloseOutlined className="TestDirectoryModal__InputClearIcon" aria-hidden />,
};

export default function RequestNewTestModal({
  initialTestName = "",
  onCancel,
  onSubmitted,
  onDone,
}) {
  const [form, setForm] = useState({
    ...INITIAL_FORM,
    test_name: initialTestName || "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const updateField = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.test_name.trim()) nextErrors.test_name = "Test name is required";
    const frequency = Number(form.test_frequency);
    if (!form.test_frequency.trim() || !Number.isInteger(frequency) || frequency <= 0) {
      nextErrors.test_frequency = "Enter a valid monthly usage";
    }
    if (!form.test_spec.trim()) nextErrors.test_spec = "Spec value is required";
    if (!form.test_unit.trim()) nextErrors.test_unit = "Units are required";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || submitting) return;
    setSubmitting(true);
    const name = form.test_name.trim();
    const payload = {
      test_name: name,
      test_frequency: Number(form.test_frequency),
      test_spec: form.test_spec.trim(),
      test_unit: form.test_unit.trim(),
      test_method: form.test_method.trim(),
      comment: form.comment.trim(),
      type: "new",
    };
    const { success, message } = await createTestRequests(payload);
    if (!isMountedRef.current) return;
    setSubmitting(false);
    if (success) {
      if (typeof onSubmitted === "function") {
        onSubmitted(payload);
      }
      setSubmitted(true);
      return;
    }
    toast.error(message || "Unable to submit test request.");
  };

  if (submitted) {
    return (
      <TestDirectoryRequestSuccessModal
        open
        message="The new test will be added to your test directory once approved."
        onClose={onCancel}
        onDone={onDone}
      />
    );
  }

  return (
    <Modal
      open
      closable
      centered
      footer={null}
      onCancel={onCancel}
      width={700}
      wrapClassName="TestDirectoryModal TestDirectoryModal--requestNew"
      className="TestDirectoryModal TestDirectoryModal--requestNew"
      closeIcon={<CloseOutlined className="SampleSubmissionModal__closeIcon" />}
      destroyOnHidden
    >
      <div className="TestDirectoryModal__RequestNew">
        <header className="TestDirectoryModal__Header">
          <div className="TestDirectoryModal__HeaderMain">
            <div className="TestDirectoryModal__HeaderTextBlock">
              <div className="TestDirectoryModal__HeaderTitleRow">
                <div className="TestDirectoryModal__HeaderTitle">Request new test</div>
              </div>
              <p className="TestDirectoryModal__HeaderNote">
                Note: The test will be available after CSM team approval.
              </p>
            </div>
          </div>
        </header>

        <div className="TestDirectoryModal__FormBody">
          <div className="TestDirectoryModal__FormFields">
            <div className="TestDirectoryModal__FieldBlock">
              <label className="TestDirectoryModal__Label" htmlFor="request-test-name">Test Name</label>
              <Input
                id="request-test-name"
                allowClear={INPUT_ALLOW_CLEAR}
                value={form.test_name}
                onChange={updateField("test_name")}
                placeholder="Type Test"
                className="TestDirectoryModal__Input"
              />
              {errors.test_name && <small className="TestDirectoryModal__Error">{errors.test_name}</small>}
            </div>

            <div className="TestDirectoryModal__FieldBlock">
              <label className="TestDirectoryModal__Label" htmlFor="request-test-frequency">
                Approximately how many times per month will you use this test?
              </label>
              <Input
                id="request-test-frequency"
                value={form.test_frequency}
                onChange={updateField("test_frequency")}
                placeholder="00"
                className="TestDirectoryModal__Input TestDirectoryModal__UsageInput"
              />
              {errors.test_frequency && (
                <small className="TestDirectoryModal__Error">{errors.test_frequency}</small>
              )}
            </div>

            <div className="TestDirectoryModal__FieldRow">
              <div className="TestDirectoryModal__Field">
                <label className="TestDirectoryModal__Label" htmlFor="request-test-spec">
                  Sensitivity/Spec value
                </label>
                <Input
                  id="request-test-spec"
                  allowClear={INPUT_ALLOW_CLEAR}
                  value={form.test_spec}
                  onChange={updateField("test_spec")}
                  placeholder="e.g., <10 CFU/g, <0.1 MPN"
                  className="TestDirectoryModal__Input"
                />
                {errors.test_spec && <small className="TestDirectoryModal__Error">{errors.test_spec}</small>}
              </div>
              <div className="TestDirectoryModal__Field">
                <label className="TestDirectoryModal__Label" htmlFor="request-test-unit">Units</label>
                <Input
                  id="request-test-unit"
                  allowClear={INPUT_ALLOW_CLEAR}
                  value={form.test_unit}
                  onChange={updateField("test_unit")}
                  placeholder="e.g., CFU/g, MPN"
                  className="TestDirectoryModal__Input"
                />
                {errors.test_unit && <small className="TestDirectoryModal__Error">{errors.test_unit}</small>}
              </div>
              <div className="TestDirectoryModal__Field">
                <label className="TestDirectoryModal__Label" htmlFor="request-test-method">
                  Test Method (Optional)
                </label>
                <Input
                  id="request-test-method"
                  allowClear={INPUT_ALLOW_CLEAR}
                  value={form.test_method}
                  onChange={updateField("test_method")}
                  placeholder="e.g., USP <61>, AOAC"
                  className="TestDirectoryModal__Input"
                />
              </div>
            </div>

            <div className="TestDirectoryModal__FieldBlock">
              <label className="TestDirectoryModal__Label" htmlFor="request-test-comment">Comments (Optional)</label>
              <Input.TextArea
                id="request-test-comment"
                value={form.comment}
                onChange={updateField("comment")}
                placeholder="Type"
                className="TestDirectoryModal__TextArea"
                rows={3}
              />
            </div>
          </div>

          <div className="TestDirectoryModal__Actions">
            <Button
              type="primary"
              htmlType="button"
              className="TestDirectoryModal__SubmitBtn"
              onClick={handleSubmit}
              loading={submitting}
            >
              Submit
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
